import React, { useMemo, useCallback } from 'react';
import { Flex, Text, Button, Tag, Collapse, Icon } from 'core/components';
import styled from 'styled-components';
import { useHotkeys } from '@blueprintjs/core';
import { SERVER_PAUSE_KINDS } from '@kentik/ui-shared/kaizen/constants';
import CodeMirror from 'core/components/CodeMirror';
import { REFUSAL_TEXT } from './util/decisionRefusal';

const ApprovalActionButton = styled(Button)`
  padding: 6px 10px;
  flex: 1;
  max-width: calc(50% - 4px);
`;

export const KaizenToolResultConfirmActions = ({
  tool,
  isAsker,
  askerName,
  onApprove,
  onApproveAndAutoApprove,
  onReject,
  onCancel,
  disabled,
  hasApprovalPreview
}) => {
  // Determine if auto-approve option should be shown based on tool configuration
  // by checking for an explicit allow_auto_approve we default to the most conservative option
  // of not showing the auto-approve button.
  const allowAutoApprove = tool.allow_auto_approve === true;
  const isServerRow = SERVER_PAUSE_KINDS.includes(tool.pause_context?.kind);
  // Only the asker approves a server, for the rest of the conversation; anyone may reject it.
  const canApprove = !isServerRow || isAsker;
  const displayName = isServerRow ? tool.pause_context.server_name : tool.tool_display_name || tool.tool_name;
  const refusalText = tool.pause_context && REFUSAL_TEXT[tool.decision_refused]?.(tool.pause_context.server_name);

  const handleApprove = useCallback(() => {
    if (!disabled) {
      onApprove();
    }
  }, [disabled, onApprove]);

  const handleApproveAndAutoApprove = useCallback(() => {
    if (!disabled) {
      onApproveAndAutoApprove();
    }
  }, [disabled, onApproveAndAutoApprove]);

  const handleReject = useCallback(() => {
    if (!disabled) {
      onReject();
    }
  }, [disabled, onReject]);

  const [isDetailsOpen, setIsDetailsOpen] = React.useState(true);

  const toggleDetails = useCallback(() => {
    setIsDetailsOpen((prev) => !prev);
  }, []);

  // Memoize hotkeys array to avoid infinitely re-binding hotkeys
  // Conditionally include auto-approve hotkey based on allowAutoApprove
  const hotkeys = useMemo(() => {
    const baseHotkeys = [];

    if (canApprove) {
      baseHotkeys.push({
        combo: '1',
        label: isServerRow ? 'Approve for this conversation' : 'Approve tool once',
        onKeyDown: handleApprove,
        preventDefault: true
      });
    }

    if (canApprove && allowAutoApprove) {
      baseHotkeys.push({
        combo: '2',
        label: 'Approve tool for conversation',
        onKeyDown: handleApproveAndAutoApprove,
        preventDefault: true
      });
    }

    baseHotkeys.push({
      combo: String(baseHotkeys.length + 1),
      label: 'Reject tool usage',
      onKeyDown: handleReject,
      preventDefault: true
    });

    return baseHotkeys;
  }, [handleApprove, handleApproveAndAutoApprove, handleReject, allowAutoApprove, canApprove, isServerRow]);

  const rejectCombo = hotkeys[hotkeys.length - 1].combo;

  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap={1}
      mt={1}
      flex={1}
      width="100%"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      <Text small>
        {!isServerRow && (
          <>
            The <strong>{displayName}</strong> tool requires your approval before execution.
          </>
        )}
        {isServerRow && canApprove && (
          <>
            AI Advisor wants to use <strong>{displayName}</strong> in this conversation.
          </>
        )}
        {!canApprove && (
          <>
            Waiting for {askerName || 'the person who asked'} to approve <strong>{displayName}</strong>.
          </>
        )}
      </Text>
      <Flex gap={2} width="72%" flexWrap="wrap">
        {canApprove && (
          <ApprovalActionButton
            intent="success"
            ariaLabel="Approve"
            text={
              <Flex alignItems="center" gap={1}>
                <Tag round>1</Tag>
                Approve
              </Flex>
            }
            onClick={onApprove}
            disabled={disabled}
          />
        )}
        {canApprove && allowAutoApprove && (
          <ApprovalActionButton
            intent="success"
            ariaLabel="Approve All"
            text={
              <Flex alignItems="center" gap={1}>
                <Tag round>2</Tag>
                Approve All
              </Flex>
            }
            onClick={onApproveAndAutoApprove}
            disabled={disabled}
          />
        )}
        <ApprovalActionButton
          intent="danger"
          ariaLabel="Reject"
          text={
            <Flex alignItems="center" gap={1}>
              <Tag round>{rejectCombo}</Tag>
              Reject
            </Flex>
          }
          onClick={onReject}
          disabled={disabled}
        />
        <Button small onClick={onCancel} disabled={disabled || !onCancel}>
          Cancel
        </Button>
      </Flex>
      {refusalText && (
        <Text small color="warning">
          {refusalText}
        </Text>
      )}
      {!hasApprovalPreview && (
        <>
          <Flex alignItems="center" gap="4px" mt={1} style={{ cursor: 'pointer' }} onClick={toggleDetails}>
            <Icon icon={isDetailsOpen ? 'caret-down' : 'caret-right'} size={12} color="muted" />
            <Text small muted>
              {isDetailsOpen ? 'Hide' : 'View'} tool details
            </Text>
          </Flex>
          <Collapse isOpen={isDetailsOpen}>
            <CodeMirror
              mode="javascript"
              value={JSON.stringify(tool.query_object, null, 2)}
              options={{
                readOnly: true
              }}
            />
          </Collapse>
        </>
      )}
    </Flex>
  );
};
